// src/pages/reporting/Forecasting.jsx
// Full monthly calendar view for forecast entry and review.
// Fetches all forecasts for the visible month in one request and renders them on the calendar.
// Clicking a day opens DayForecastForm in the right panel and also updates the parent date
// (via onDateChange) so the KPI stat cards at the top of the page refresh for that day.
import { useState, useEffect, useMemo, useRef } from "react";
import { Card, ForecastCalendar, ForecastingSummaryBar, Dropdown, Loading } from "@common";
import { useForecasts, useClinics, useClinicDetail, useForecastMtd } from "@api/hooks";
import { useAuth } from "@api/useAuth";
import {
  isClinicScopedUser,
  isOrganizerOrAdmin,
  isObserver,
  isViewOnlyRole,
  canAccessAllClinics,
  torontoToday,
  torontoDateParts,
} from "@utils";
import { clinicLabel, getForecastMaxDate } from "@utils/utils";
import DayForecastForm from "./DayForecastForm";

// Returns the first and last date of a given calendar month as YYYY-MM-DD strings
// so the forecasts query can fetch all days at once in one request instead of per-day
function getMonthBounds(year, month) {
  const mm = String(month + 1).padStart(2, "0");
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    dateFrom: `${year}-${mm}-01`,
    dateTo: `${year}-${mm}-${String(lastDay).padStart(2, "0")}`,
  };
}

export default function Forecasting({
  clinicId, // Clinic code passed down from the parent (e.g. Admin's multi-select)
  date, // Currently selected date (YYYY-MM-DD); controls the day panel on the right
  clinics: clinicsProp, // Optional pre-loaded clinic list for building the name map
  onDateChange, // Callback fired when the user clicks a calendar day
  hideClinicSelector = false, // True when the parent controls clinic selection via its own filter
  hideSummary = false, // True when the parent renders an aggregated summary instead
  label = null, // Optional heading shown above the calendar (e.g. clinic name)
  onSummaryChange = null, // Callback that lifts monthlySummary up to Admin for aggregation
  calYearOverride = null, // When Admin controls all blocks together, this drives the year
  calMonthOverride = null, // When Admin controls all blocks together, this drives the month
  onCalMonthChange = null, // Fired on prev/next so Admin can sync all clinic blocks together
  forecastsDataOverride = null, // Pre-fetched items from the Admin batch call — when provided,
  // the individual useForecasts call is skipped entirely so the
  // page makes 1 request instead of N (one per selected clinic).
}) {
  // Internal date state used when no date/onDateChange props are provided (e.g. multi-clinic
  // views where each block manages its own selected day independently).
  const [internalDate, setInternalDate] = useState(null);
  const activeDate = date ?? internalDate;
  function handleDateChange(d) {
    setInternalDate(d);
    onDateChange?.(d);
  }

  // Reset the internal selected day whenever the shared month changes so the right panel
  // doesn't show stale day-level data from a different month.
  useEffect(() => {
    if (calYearOverride != null || calMonthOverride != null) setInternalDate(null);
  }, [calYearOverride, calMonthOverride]);
  const { user } = useAuth();

  // Only Organizers, Admins, Observers, and clinic-scoped users can pick a clinic
  const canSelectClinic = isOrganizerOrAdmin(user) || isObserver(user) || isClinicScopedUser(user);

  // selectedClinic is the locally controlled value inside the dropdown
  const [selectedClinic, setSelectedClinic] = useState(clinicId ?? "");

  // activeClinic is what actually drives data fetching:
  // - When hideClinicSelector=true the parent owns the value (clinicId)
  // - Otherwise the dropdown controls it (selectedClinic)
  const activeClinic = hideClinicSelector
    ? (clinicId ?? "")
    : canSelectClinic
      ? selectedClinic
      : (clinicId ?? "");

  // Keep local selectedClinic in sync whenever the parent changes clinicId
  useEffect(() => {
    setSelectedClinic(clinicId ?? "");
  }, [clinicId]);

  // Calendar month state — seeded from the override (Admin shared nav) or the date prop
  const [calYear, setCalYear] = useState(() => {
    if (calYearOverride != null) return calYearOverride;
    if (activeDate) return new Date(activeDate + "T00:00:00").getFullYear();
    return torontoDateParts().year;
  });
  const [calMonth, setCalMonth] = useState(() => {
    if (calMonthOverride != null) return calMonthOverride;
    if (activeDate) return new Date(activeDate + "T00:00:00").getMonth(); // 0-indexed
    return torontoDateParts().month - 1;
  });

  // Keep the calendar month in sync when the parent's selected date moves to a different month
  // (e.g. user navigates back to this tab with a date from a previous month stored in state).
  // Skipped when Admin is driving the calendar via calYearOverride/calMonthOverride.
  useEffect(() => {
    if (!activeDate) return;
    if (calYearOverride != null || calMonthOverride != null) return;
    const d = new Date(activeDate + "T00:00:00");
    if (d.getFullYear() !== calYear || d.getMonth() !== calMonth) {
      setCalYear(d.getFullYear());
      setCalMonth(d.getMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate, calYearOverride, calMonthOverride]);

  // Sync local calendar state when Admin changes the shared month (e.g. user clicks ‹ on any block)
  useEffect(() => {
    if (calYearOverride != null) setCalYear(calYearOverride);
  }, [calYearOverride]);

  useEffect(() => {
    if (calMonthOverride != null) setCalMonth(calMonthOverride);
  }, [calMonthOverride]);

  // Clinic option lists — Org/Admin/Observer can read all clinics; scoped users see only theirs
  const canReadAllClinics = canAccessAllClinics(user);
  const { data: clinicsData } = useClinics({}, { enabled: canReadAllClinics });
  const userClinics = user?.clinic || [];
  const firstClinic = userClinics[0] || "";
  const { data: clinicDetailData } = useClinicDetail(firstClinic, {
    enabled: !canReadAllClinics && !!firstClinic,
  });

  // Build a code → name map from whatever clinic data is available
  const clinicNameMap = new Map(
    (clinicsProp || []).map((c) => [c.clinic, c.clinic_name || c.name || ""]),
  );
  if (clinicDetailData?.clinic) {
    const cd = clinicDetailData.clinic;
    if (cd.clinic && cd.name) clinicNameMap.set(cd.clinic, cd.name);
  }

  // Build the sorted dropdown options depending on the user's access level
  const clinicOptions = (
    canReadAllClinics
      ? (clinicsData?.results || clinicsData || []).map((c) => ({
          value: c.clinic,
          label: clinicLabel(c),
        }))
      : userClinics.map((code) => {
          const name = (clinicNameMap.get(code) || "").trim();
          return { value: code, label: name ? `${name} (${code})` : code };
        })
  ).sort((a, b) => a.label.localeCompare(b.label));

  // Derive the date range for the currently visible calendar month
  const { dateFrom, dateTo } = useMemo(
    () => getMonthBounds(calYear, calMonth),
    [calYear, calMonth],
  );

  // When the Admin batch call provides data (forecastsDataOverride is an array), skip the
  // individual fetch by passing an empty clinic — the hook's `enabled` check requires
  // `!!filters.clinic`, so an empty string disables it without violating Rules of Hooks.
  const hasBatchOverride = Array.isArray(forecastsDataOverride);
  const { data: forecastsData, isLoading: forecastsLoading } = useForecasts({
    clinic: hasBatchOverride ? "" : activeClinic,
    date_from: dateFrom,
    date_to: dateTo,
  });

  // Build a date → forecast object map so ForecastCalendar can look up each day in O(1).
  // Prefer the batch-provided data when available; fall back to the individual fetch result.
  const forecastsByDate = useMemo(() => {
    const items = hasBatchOverride
      ? forecastsDataOverride
      : forecastsData?.results || forecastsData || [];
    return items.reduce((acc, f) => {
      acc[f.date] = f;
      return acc;
    }, {});
  }, [forecastsData, forecastsDataOverride, hasBatchOverride]);

  // The forecast record (if any) for the currently selected day
  const existingForecast = activeDate ? forecastsByDate[activeDate] || null : null;

  // Current Toronto date — used for todayStr and isPastMonth
  const todayStr = torontoToday();
  const now = torontoDateParts();

  // isPastMonth: the calendar is showing a month that has already ended
  const isPastMonth = calYear < now.year || (calYear === now.year && calMonth < now.month - 1);

  // Monthly summary stats shown in the bar above the calendar.
  // - mtd: actual production from the 1st of the month up to (and for past months: including) today
  // - forecastFromToday: sum of all saved forecast values from today through month-end
  // - monthEnd: mtd + forecastFromToday — the projected end-of-month total
  const monthlySummary = useMemo(() => {
    let forecastFromToday = 0;
    let latestMtd = 0;
    let lastActualEntry = null; // Tracks the most recent entry on or before today

    const entries = Object.entries(forecastsByDate).sort(([a], [b]) => a.localeCompare(b));
    for (const [dateKey, f] of entries) {
      // Daily forecast value: prefer the DDS+HYG split; fall back to the legacy total field
      const daily =
        f.forecast_dds != null && f.forecast_hyg != null
          ? parseFloat(f.forecast_dds || 0) + parseFloat(f.forecast_hyg || 0)
          : parseFloat(f.forecast || 0);

      // Accumulate forecast amounts for today and future days
      if (dateKey >= todayStr) forecastFromToday += daily;

      // Track the most recent MTD figure on or before today
      if (dateKey <= todayStr && f.mtd_production != null) {
        latestMtd = parseFloat(f.mtd_production);
        lastActualEntry = f;
      }
    }

    // For past months the backend's mtd_production on any given day is the cumulative sum
    // up to but NOT including that day (e.g. April 30 MTD = April 1–29 production).
    // Adding the last entry's actual split (mtd_dds + mtd_hyg) fills in the final day so
    // the displayed MTD represents the full completed month rather than stopping one day short.
    if (isPastMonth && lastActualEntry) {
      latestMtd +=
        parseFloat(lastActualEntry.mtd_dds || 0) + parseFloat(lastActualEntry.mtd_hyg || 0);
    }

    return { mtd: latestMtd, forecastFromToday, monthEnd: latestMtd + forecastFromToday };
  }, [forecastsByDate, todayStr, isPastMonth]);

  // Lift the summary to the parent (Admin) so it can aggregate across multiple clinics.
  // A ref is used to avoid stale closures when the callback identity changes between renders.
  const onSummaryChangeRef = useRef(onSummaryChange);
  onSummaryChangeRef.current = onSummaryChange;

  useEffect(() => {
    onSummaryChangeRef.current?.({ ...monthlySummary, isPastMonth });
  }, [monthlySummary.mtd, monthlySummary.forecastFromToday, monthlySummary.monthEnd, isPastMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Blank days (weekends, no forecast entered) have no record in forecastsByDate, so
  // mtd_production would be 0. Fire a separate /api/forecasts/mtd/ request for those days
  // so the right-panel form still shows the correct MTD even when no forecast record exists.
  // Only fires when existingForecast is null to avoid a redundant double-fetch.
  const { data: mtdData } = useForecastMtd(
    { clinic: activeClinic, date: activeDate },
    { enabled: !!activeClinic && !!activeDate && !existingForecast },
  );

  // Resolved MTD for the selected day: prefer the forecast record, fall back to the dedicated
  // MTD endpoint for blank/weekend days, and clamp to 0 to guard against stale nulls.
  const mtd = Math.max(0, existingForecast?.mtd_production ?? mtdData?.mtd_production ?? 0);

  // Past dates and view-only users cannot submit or edit forecasts
  const canEditSelectedDate = !isViewOnlyRole(user) && !!activeDate && activeDate >= todayStr;

  // Hard ceiling on how far ahead a forecast can be entered
  const forecastMaxDate = useMemo(getForecastMaxDate, []);

  // Navigate the calendar to the previous month.
  // Also fires onCalMonthChange so Admin can sync all clinic blocks to the same month.
  function handlePrevMonth() {
    const newYear = calMonth === 0 ? calYear - 1 : calYear;
    const newMonth = calMonth === 0 ? 11 : calMonth - 1;
    setCalYear(newYear);
    setCalMonth(newMonth);
    onCalMonthChange?.(newYear, newMonth);
  }

  // Navigate the calendar to the next month.
  // Also fires onCalMonthChange so Admin can sync all clinic blocks to the same month.
  function handleNextMonth() {
    const newYear = calMonth === 11 ? calYear + 1 : calYear;
    const newMonth = calMonth === 11 ? 0 : calMonth + 1;
    setCalYear(newYear);
    setCalMonth(newMonth);
    onCalMonthChange?.(newYear, newMonth);
  }

  return (
    <Card className="mt-4 mx-28">
      <div className="space-y-5">
        {/* Optional heading — used in the Admin multi-clinic view to label each calendar block */}
        {label && <h3 className="text-base font-semibold text-palette-gray-heading">{label}</h3>}

        {/* Clinic selector — hidden when the parent controls selection via its own filter */}
        {!hideClinicSelector &&
          (canSelectClinic ? (
            <div className="max-w-sm">
              <Dropdown
                label="Clinic"
                name="clinic"
                options={clinicOptions}
                value={activeClinic}
                onChange={setSelectedClinic}
                placeholder="Select a clinic"
                required
              />
            </div>
          ) : activeClinic ? (
            <p className="text-sm text-palette-gray-secondary">
              Clinic: <span className="font-medium text-palette-gray-text">{activeClinic}</span>
            </p>
          ) : null)}

        {/* Empty state shown when no clinic has been selected yet */}
        {!activeClinic ? (
          <p className="py-8 text-center text-sm text-palette-gray-placeholder">
            {hideClinicSelector
              ? "Select a clinic from the filter above to view the forecast calendar"
              : "Select a clinic to view the forecast calendar"}
          </p>
        ) : (
          <div className="space-y-5">
            {/* Per-clinic monthly summary bar — hidden when Admin renders its own aggregated bar */}
            {!hideSummary && (
              <ForecastingSummaryBar
                clinicCount={1}
                mtd={monthlySummary.mtd}
                forecastFromToday={monthlySummary.forecastFromToday}
                monthEnd={monthlySummary.monthEnd}
                isPastMonth={isPastMonth}
              />
            )}

            <div className="flex gap-6 items-start">
              {/* Left: monthly calendar grid */}
              <div className="flex-1 min-w-0 relative">
                <ForecastCalendar
                  year={calYear}
                  month={calMonth}
                  forecastsByDate={forecastsByDate}
                  selectedDate={activeDate}
                  onDayClick={handleDateChange}
                  onPrevMonth={handlePrevMonth}
                  onNextMonth={handleNextMonth}
                  maxDate={forecastMaxDate}
                  clinic={activeClinic}
                  canEdit={!isViewOnlyRole(user)}
                />
                {/* Loading overlay while the month's forecasts are being fetched */}
                {forecastsLoading && !forecastsData && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-lg">
                    <Loading size="sm" text="Loading..." />
                  </div>
                )}
              </div>

              {/* Right: day-level forecast details and entry form */}
              <div className="w-96 flex-shrink-0 border-l border-palette-gray-subtle pl-8">
                {activeDate ? (
                  <DayForecastForm
                    clinic={activeClinic}
                    date={activeDate}
                    existingForecast={existingForecast}
                    mtd={mtd}
                    canEdit={canEditSelectedDate}
                    forecastsByDate={forecastsByDate}
                  />
                ) : (
                  // Placeholder shown before the user selects a day
                  <div className="flex h-48 items-center justify-center text-center text-sm text-palette-gray-placeholder">
                    Click a day on the calendar to view forecast details
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
